import Dialog from '@components/atoms/dialog/dialog';
import TagContainer from '@enums/tagContainer';
import { useAnalytics } from '@context/analytics.context';
import { useNegotiation } from '@context/negotiation.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useLocalStorage from '@hooks/use-local-storage';
import { getNegotiationIdFromUrl } from '@helpers/utils/mmao';
import React, { HTMLProps, useEffect, useMemo, useState } from 'react';
import { useBucket } from '@context/bucket.context';
import { LDState } from '@components/bucketTest/types';
import { useRouter } from 'next/router';
import {
  useActiveNegotiation,
  useCurrentNegotiation,
  useNegotiationsByProduct,
} from '@hooks/negotiations/useNegotationQueries';
import { MmaoDialogStyles } from '@constants/mmao';
import dynamic from 'next/dynamic';
import useProductQuery from '@hooks/product/use-product-query';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import useUser from '@hooks/user/use-user';
import CTAButtonsBlock from './components/CTA-buttons-block';
import CTAMmaoBlock from './components/CTA-mmao-block';
import { CTAProductContext } from './context';

const MMAOModal = dynamic(() => import('@components/molecules/products/mmao-modal/mmao-modal'), {
  ssr: false,
});

const CTAMessageBlock = dynamic(() => import('./components/CTA-message-block'), {
  ssr: false,
});

type CTAProductProps = Pick<HTMLProps<HTMLDivElement>, 'className'>;

const CTAProduct: React.FC<CTAProductProps> = ({ className }): React.JSX.Element => {
  const { sendEvent } = useAnalytics();
  const { state, features } = useBucket();
  const { sendAnalyticEvent: sendMMAOAnalyticEvents } = useAnalyticEvents('mmao');
  const { negotiation } = useNegotiation();
  const { isAuthenticated } = useUser();
  const { product, userTransactionRole } = useProductQuery();
  const { isHeroPdpStyles } = useHeroPdpStyles();

  const [sellerBackHolidayAlerts, setSellerBackHolidayAlerts] = useLocalStorage('sellerBackHolidayAlerts', []);

  const [openMmaoModal, setOpenMmaoModal] = useState<boolean>(false);
  const [holidaysEndAlertId, setHolidaysEndAlertId] = useState(null);
  const [showFpdSection, setShowFpdSection] = useState<boolean>(false);
  const [canRestartNegotiation, setCanRestartNegotiation] = useState<boolean>(true);
  const mmaoModalType = useRouter().query?.modalType;
  const negotiationId = useRouter().query?.negotiation_id as string | undefined;
  const showFpd = useRouter().query?.showFpd === 'true';

  const isSeller: boolean = userTransactionRole === 'seller';

  const currentUrlPath = useMemo(() => (typeof window !== 'undefined' ? window?.location?.href : ''), [typeof window]);
  const isEnableFpd = useMemo(() => currentUrlPath?.includes('showFpd=true'), [currentUrlPath]);

  const currentNegotiationId = useMemo(() => getNegotiationIdFromUrl(currentUrlPath), [currentUrlPath]);

  useCurrentNegotiation({
    negotiationId: currentNegotiationId,
    enabled: isAuthenticated && currentUrlPath?.includes('negotiation_id='),
    onQuery: () => {
      setShowFpdSection(isEnableFpd);
    },
    onSuccess: () => {
      setCanRestartNegotiation(false);
      setOpenMmaoModal(true);
    },
  });

  useCurrentNegotiation({
    negotiationId: negotiationId,
    enabled: (mmaoModalType || showFpd) && negotiationId && isAuthenticated,
    onQuery: () => {
      setShowFpdSection(showFpd);
    },
    onSuccess: () => {
      setOpenMmaoModal(true);
    },
  });

  useActiveNegotiation({
    productId: product.id,
    enabled: isAuthenticated && product.negotiable && !isSeller,
  });

  useNegotiationsByProduct({
    productId: product.id,
    enabled: isAuthenticated && product.negotiable && isSeller,
  });

  const shouldShowMMAOMessageBlock =
    Boolean(negotiation?.buyerRemainingOffers || negotiation?.sellerRemainingOffers) ||
    negotiation?.nextActions?.length;

  const trackMmaoClick = () => {
    sendMMAOAnalyticEvents('pdp_make_an_offer');
    if (isAuthenticated) {
      setOpenMmaoModal(true);
    }
  };

  const closeMMAOModal = (): void => {
    setOpenMmaoModal(false);
    sendEvent({
      type: 'event',
      container: TagContainer.Analytics,
      payload: {
        event: 'popinClose',
        popinName: 'MMAO',
      },
    });
  };

  const showDisplayMmaoOfferBanner = useMemo<boolean | unknown>(() => {
    if ([LDState.INITIALIZING].includes(state)) {
      return false;
    }
    if ([LDState.SUCCESS].includes(state)) {
      return features.displayMmaoOfferBannerPdp;
    } else {
      return false;
    }
  }, [state, features]);

  const isEnableMmaoOffersBannerControlGroupEnabled = useMemo<boolean | unknown>(() => {
    if ([LDState.INITIALIZING].includes(state)) {
      return false;
    }
    if ([LDState.SUCCESS].includes(state)) {
      return features.enableMmaoOffersBannerControlGroupPdp;
    } else {
      return false;
    }
  }, [state, features]);

  const shouldShowMMAOBanner = useMemo<boolean>(() => {
    const isBannerEligibleToShow = !isSeller && product?.buyersWhoHaveActiveNegotiation?.length > 0;
    const ldConfigToShowBanner = Number(product?.id) % 2 > 0 && showDisplayMmaoOfferBanner;
    return isEnableMmaoOffersBannerControlGroupEnabled
      ? !!(isBannerEligibleToShow && ldConfigToShowBanner)
      : isBannerEligibleToShow;
  }, [isEnableMmaoOffersBannerControlGroupEnabled, showDisplayMmaoOfferBanner, product, isSeller]);

  useEffect(() => {
    sellerBackHolidayAlerts.forEach((item) => {
      const propertyNames = Object.getOwnPropertyNames(item);
      if (propertyNames[0] === product.id) {
        setHolidaysEndAlertId(item[propertyNames[0]]);
      }
    });
  }, []);

  return (
    <CTAProductContext
      value={{
        sellerBackHolidayAlerts,
        holidaysEndAlertId,
        setSellerBackHolidayAlerts,
        setHolidaysEndAlertId,
        trackMmaoClick,
      }}
    >
      {shouldShowMMAOMessageBlock && <CTAMessageBlock />}
      {shouldShowMMAOBanner && (
        <CTAMmaoBlock
          trackingFrom="mmao_ongoing_banner_pdp"
          showBuyNow={false}
          applyTopMargin={!isHeroPdpStyles}
          product={product}
          showMMAOBanner={shouldShowMMAOBanner}
        />
      )}
      {!isSeller && <CTAButtonsBlock customClass={className} />}
      <Dialog
        closeDialog={closeMMAOModal}
        key={`openMmaoModal${openMmaoModal}`}
        isOpen={openMmaoModal}
        hasCloseIcon
        styles={{
          content: MmaoDialogStyles,
        }}
      >
        <MMAOModal
          canRestartNegotiation={canRestartNegotiation}
          product={product}
          onClose={closeMMAOModal}
          showFpdSection={showFpdSection}
        />
      </Dialog>
    </CTAProductContext>
  );
};

export default CTAProduct;
